import type { User } from "@supabase/supabase-js";
import { supabase } from "@/lib/supabase";
import { logAuthError, createLogContext } from "@/lib/utils/logger";

/**
 * Vérifie si l'email d'un utilisateur est vérifié
 *
 * Centralise la logique de vérification pour éviter les incohérences
 * dans différents fichiers du codebase.
 *
 * @param user - L'utilisateur à vérifier (peut être null)
 * @returns true si l'email est vérifié, false sinon
 *
 * @example
 * ```typescript
 * const { data: { user } } = await supabase.auth.getUser();
 * if (!isEmailVerified(user)) {
 *   navigate("/auth/verify-email");
 * }
 * ```
 */
export function isEmailVerified(user: User | null): boolean {
  if (!user) return false;

  const confirmedAt = user.email_confirmed_at;

  // email_confirmed_at est une string ISO date ou null dans Supabase
  // Vérifier que email_confirmed_at existe et n'est pas vide
  if (confirmedAt === null || confirmedAt === undefined || confirmedAt === "") {
    return false;
  }

  // Si c'est une string non vide, l'email est vérifié
  if (typeof confirmedAt === "string" && confirmedAt.trim().length > 0) {
    return true;
  }

  return false;
}

/**
 * Comprehensive email verification check
 * Checks both auth.users.email_confirmed_at and profiles.is_email_verified
 * Returns true only if BOTH indicate verification
 * 
 * This provides defense-in-depth by checking both sources of truth
 * 
 * @param user - The user to verify (can be null)
 * @returns Promise<boolean> - true if email is verified in both auth.users and profiles
 * 
 * @example
 * ```typescript
 * const { data: { user } } = await supabase.auth.getUser();
 * const verified = await isEmailVerifiedComprehensive(user);
 * if (!verified) {
 *   navigate("/auth/verify-email");
 * }
 * ```
 */
export async function isEmailVerifiedComprehensive(user: User | null): Promise<boolean> {
  if (!user) return false;
  
  // Primary check: auth.users.email_confirmed_at
  const authVerified = isEmailVerified(user);
  
  if (!authVerified) return false;
  
  // Secondary check: profiles.is_email_verified (should be synced via trigger)
  try {
    const { data: profile, error } = await supabase
      .from("profiles")
      .select("is_email_verified")
      .eq("id", user.id)
      .single();
    
    if (error || !profile) {
      // If profile doesn't exist, trust auth.users
      return authVerified;
    }
    
    // Both must be true for comprehensive verification
    return authVerified && profile.is_email_verified === true;
  } catch (err) {
    // On error, fall back to auth check only
    logAuthError(
      "Error checking profile email verification",
      createLogContext(user.id, "isEmailVerifiedComprehensive", "auth-helpers"),
      err instanceof Error ? err : undefined
    );
    return authVerified;
  }
}

/**
 * Vérifie si le profil utilisateur est complet
 * 
 * Phase 1 : Implémentation authentification Google
 * 
 * Un profil est considéré comme complet si :
 * - Le rôle est défini
 * - Pour les owners : agency_name est présent dans le profil
 * - Pour les renters : first_name et last_name sont présents
 * 
 * @param user - L'utilisateur Supabase à vérifier
 * @returns Promise<boolean> - true si le profil est complet, false sinon
 */
export async function checkProfileComplete(user: User | null): Promise<boolean> {
  if (!user) return false;

  try {
    const { data: profile, error } = await supabase
      .from("profiles")
      .select("role, agency_name, first_name, last_name")
      .eq("id", user.id)
      .single();

    if (error || !profile) {
      // Profil n'existe pas encore
      return false;
    }

    // Vérifier que le rôle est défini
    if (!profile.role) {
      return false;
    }

    // Si owner, vérifier que agency_name est défini
    if (profile.role === "owner" && !profile.agency_name) {
      return false;
    }

    // Pour les renters, vérifier que first_name et last_name sont présents
    if (profile.role === "renter" && (!profile.first_name || !profile.last_name)) {
      return false;
    }

    return true;
  } catch (error) {
    logAuthError(
      "Error checking profile complete",
      createLogContext(user?.id, "checkProfileComplete", "auth-helpers"),
      error instanceof Error ? error : undefined
    );
    return false;
  }
}

